import React, { useEffect, useState } from 'react';
import {
    Row, Col, Button, Modal, Form, Input, InputNumber,
    Switch, notification, Popconfirm, Tag, Space, Tooltip, Typography,
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
    UndoOutlined, UsergroupAddOutlined,
} from '@ant-design/icons';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApi from '../../api/adminAxios';

const { TextArea } = Input;
const { Text } = Typography;

const tierColors = {
    free: '#64748B', basic: '#3B82F6', premium: '#AD1457', ultimate: '#F59E0B',
};

export default function AdminPlans() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ open: false, editing: null });
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [form] = Form.useForm();

    const fetchPlans = () => {
        setLoading(true);
        adminApi.get('/admin/plans')
            .then((res) => setPlans(res.data.data))
            .catch(() => notification.error({ message: 'Failed to Load', description: 'Could not fetch subscription plans. Please try again.' }))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchPlans(); }, []);

    const openCreate = () => {
        form.resetFields();
        form.setFieldsValue({ currency: 'INR', is_active: true, is_popular: false, contact_numbers: 0, sort_order: plans.length });
        setModal({ open: true, editing: null });
    };

    const openEdit = (plan) => {
        form.setFieldsValue({
            ...plan,
            features: (plan.features || []).join('\n'),
            limitations: (plan.limitations || []).join('\n'),
        });
        setModal({ open: true, editing: plan });
    };

    const handleSave = async (values) => {
        setSaving(true);
        // Parse features/limitations from newline-separated text
        const payload = {
            ...values,
            features: values.features
                ? values.features.split('\n').map((s) => s.trim()).filter(Boolean)
                : [],
            limitations: values.limitations
                ? values.limitations.split('\n').map((s) => s.trim()).filter(Boolean)
                : [],
        };

        try {
            if (modal.editing) {
                await adminApi.put(`/admin/plans/${modal.editing.id}`, payload);
                notification.success({ message: 'Plan Updated', description: 'The subscription plan has been updated successfully.' });
            } else {
                await adminApi.post('/admin/plans', payload);
                notification.success({ message: 'Plan Created', description: 'The new subscription plan has been created successfully.' });
            }
            setModal({ open: false, editing: null });
            fetchPlans();
        } catch (err) {
            const errors = err.response?.data?.errors;
            if (errors) {
                Object.values(errors).flat().forEach((m) => notification.error({ message: 'Validation Error', description: m }));
            } else {
                notification.error({ message: 'Failed to Save', description: 'Could not save the plan. Please try again.' });
            }
        } finally {
            setSaving(false);
        }
    };

    const toggleStatus = async (plan) => {
        setTogglingId(plan.id);
        try {
            await adminApi.patch(`/admin/plans/${plan.id}/toggle-status`);
            notification.success({ message: `Plan ${plan.is_active ? 'Deactivated' : 'Activated'}`, description: `"${plan.name}" has been ${plan.is_active ? 'deactivated' : 'activated'} successfully.` });
            fetchPlans();
        } catch (_) {
            notification.error({ message: 'Failed to Update Status', description: 'Could not change the plan status. Please try again.' });
        } finally {
            setTogglingId(null);
        }
    };

    const deletePlan = async (id) => {
        try {
            await adminApi.delete(`/admin/plans/${id}`);
            notification.success({ message: 'Plan Deleted', description: 'The subscription plan has been removed successfully.' });
            fetchPlans();
        } catch (err) {
            notification.error({ message: 'Failed to Delete', description: err.response?.data?.message || 'Could not delete the plan. Please try again.' });
        }
    };

    const restorePlan = async (id) => {
        try {
            await adminApi.post(`/admin/plans/${id}/restore`);
            notification.success({ message: 'Plan Restored', description: 'The subscription plan has been restored and is now active.' });
            fetchPlans();
        } catch (_) {
            notification.error({ message: 'Failed to Restore', description: 'Could not restore the plan. Please try again.' });
        }
    };

    return (
        <AdminLayout title="Plans">
            <div className="admin-page-header">
                <h1 className="admin-page-title">Subscription Plans</h1>
                <p className="admin-page-subtitle">Manage pricing plans, features, and availability</p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, gap: 10 }}>
                <Button icon={<ReloadOutlined />} onClick={fetchPlans} loading={loading}>Refresh</Button>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreate}
                    style={{ background: 'var(--gradient-primary)', border: 'none' }}
                >
                    New Plan
                </Button>
            </div>

            <Row gutter={[16, 16]}>
                {loading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <Col key={i} xs={24} sm={12} lg={6}>
                            <div className="admin-plan-card" style={{ height: 280, background: '#f5f5f5' }} />
                        </Col>
                    ))
                    : plans.map((plan) => {
                        const color = tierColors[plan.plan_id] || 'var(--color-primary)';
                        const isDeleted = !!plan.deleted_at;
                        return (
                            <Col key={plan.id} xs={24} sm={12} lg={6} style={{ display: 'flex' }}>
                                <div className="admin-plan-card" style={{
                                    opacity: isDeleted ? 0.55 : 1,
                                    borderColor: isDeleted ? 'var(--color-border)' : undefined,
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}>
                                    <div className="admin-plan-card-top" style={{ background: color }} />

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                        <div>
                                            <Text strong style={{ fontSize: 15, color: 'var(--color-text-primary)' }}>
                                                {plan.name}
                                            </Text>
                                            <div style={{ fontSize: 11, color, fontWeight: 700, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                {plan.plan_id}
                                            </div>
                                        </div>
                                        <Space size={4}>
                                            {plan.is_popular && <Tag color="gold">Popular</Tag>}
                                            {isDeleted
                                                ? <Tag color="red">Deleted</Tag>
                                                : plan.is_active ? <Tag color="green">Active</Tag> : <Tag color="orange">Inactive</Tag>
                                            }
                                        </Space>
                                    </div>

                                    <div style={{ marginBottom: 10 }}>
                                        <Text style={{ fontSize: 26, fontWeight: 800, color }}>
                                            {plan.price === 0 ? 'Free' : `₹${Number(plan.price).toLocaleString('en-IN')}`}
                                        </Text>
                                        {plan.price > 0 && (
                                            <Text style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 4 }}>
                                                / {plan.duration_days} days
                                            </Text>
                                        )}
                                    </div>

                                    <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--color-text-secondary)' }}>
                                        {plan.description}
                                    </div>

                                    <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <Tag>📞 {plan.contact_numbers} contacts</Tag>
                                        {plan.savings && <Tag color="green">{plan.savings}</Tag>}
                                    </div>

                                    <div style={{ marginBottom: 12 }}>
                                        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 700, textTransform: 'uppercase' }}>
                                            Features
                                        </div>
                                        {(plan.features || []).slice(0, 3).map((f, i) => (
                                            <div key={i} style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'flex', gap: 6, marginBottom: 2 }}>
                                                <span style={{ color: '#10B981' }}>✓</span> {f}
                                            </div>
                                        ))}
                                        {(plan.features || []).length > 3 && (
                                            <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                                                +{plan.features.length - 3} more
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ marginTop: 'auto', paddingTop: 12 }}>
                                        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 10, minHeight: 18 }}>
                                            <UsergroupAddOutlined />
                                            {plan.subscriptions_count ?? 0} subscriber{(plan.subscriptions_count ?? 0) !== 1 ? 's' : ''}
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            {!isDeleted ? (
                                                <>
                                                    <Tooltip title={plan.is_active ? 'Deactivate' : 'Activate'}>
                                                        <Switch
                                                            size="small"
                                                            checked={plan.is_active}
                                                            loading={togglingId === plan.id}
                                                            onChange={() => toggleStatus(plan)}
                                                        />
                                                    </Tooltip>
                                                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(plan)}>
                                                        Edit
                                                    </Button>
                                                    <Popconfirm
                                                        title="Delete this plan? Active subscribers will keep their plan until expiry."
                                                        onConfirm={() => deletePlan(plan.id)}
                                                        okButtonProps={{ danger: true }}
                                                    >
                                                        <Button size="small" icon={<DeleteOutlined />} danger>Delete</Button>
                                                    </Popconfirm>
                                                </>
                                            ) : (
                                                <Button size="small" icon={<UndoOutlined />} onClick={() => restorePlan(plan.id)}>
                                                    Restore
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        );
                    })
                }
            </Row>

            {/* Create / Edit modal */}
            <Modal
                title={modal.editing ? `Edit: ${modal.editing.name}` : 'Create New Plan'}
                open={modal.open}
                onCancel={() => setModal({ open: false, editing: null })}
                onOk={() => form.submit()}
                okText={modal.editing ? 'Save Changes' : 'Create Plan'}
                confirmLoading={saving}
                width={Math.min(640, window.innerWidth - 32)}
                centered
                styles={{ body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 } }}
            >
                <Form form={form} layout="vertical" onFinish={handleSave}>
                    <Row gutter={12}>
                        <Col span={14}>
                            <Form.Item name="name" label="Plan Name" rules={[{ required: true }]}>
                                <Input placeholder="e.g. Premium Gold" />
                            </Form.Item>
                        </Col>
                        <Col span={10}>
                            <Form.Item name="plan_id" label="Plan ID (unique slug)" rules={[
                                { required: true },
                                { pattern: /^[a-z0-9_]+$/, message: 'Lowercase, numbers, underscores only' },
                            ]}>
                                <Input placeholder="e.g. premium_gold" disabled={!!modal.editing} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label="Description">
                        <Input placeholder="Short description of the plan" />
                    </Form.Item>

                    <Row gutter={12}>
                        <Col span={8}>
                            <Form.Item name="price" label="Price (₹)" rules={[{ required: true }]}>
                                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="duration_days" label="Duration (days)" rules={[{ required: true }]}>
                                <InputNumber min={1} style={{ width: '100%' }} placeholder="30" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="contact_numbers" label="Contact Numbers">
                                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="color" label="Card Color">
                                <Input placeholder="#AD1457" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="savings" label="Savings Label">
                                <Input placeholder="e.g. Save 30%" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="features" label={
                        <span>Features <Text type="secondary" style={{ fontSize: 12 }}>(one per line)</Text></span>
                    }>
                        <TextArea rows={5} placeholder={"View full contact details\nUnlimited profile views\nPriority match recommendations"} />
                    </Form.Item>

                    <Form.Item name="limitations" label={
                        <span>Limitations <Text type="secondary" style={{ fontSize: 12 }}>(one per line)</Text></span>
                    }>
                        <TextArea rows={3} placeholder={"No AI matching\nLimited to 5 messages/day"} />
                    </Form.Item>

                    <Row gutter={12}>
                        <Col span={8}>
                            <Form.Item name="sort_order" label="Sort Order">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="is_active" label="Active" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="is_popular" label="Mark as Popular" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </AdminLayout>
    );
}
